import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Combobox, type ComboboxOption, MultiSelect, type MultiSelectOption } from "@tellescope/ui";
import { useUsersQuery } from "@tellescope/tanstack-query";
import { formatName, type FreeformFilter } from "@tellescope/utils";
import { EnduserIdFilterInput } from "./EnduserIdFilterInput";

/** Filter fields that {@link TicketsFilterValueInput} renders a specialized value editor for. */
export const TICKETS_CUSTOM_VALUE_FIELDS = ["enduserId", "priority", "stage", "owner", "tags"];

export interface TicketsFilterValueInputProps {
  filter: FreeformFilter;
  onChange: (filter: FreeformFilter) => void;
  stageOptions: Array<{ value: string; label: string }>;
  priorityOptions: Array<{ value: string; label: string }>;
  tagOptions: string[];
}

const VALUE_INPUT_CLASS_NAME = "w-full bg-white text-xs font-light";

/**
 * Field-specific value editor for a single tickets filter condition. Only render this for fields in
 * {@link TICKETS_CUSTOM_VALUE_FIELDS}; anything else has no specialized editor and should fall
 * through to `FilterBlockEditor`'s default input.
 */
export const TicketsFilterValueInput = ({
  filter,
  onChange,
  stageOptions,
  priorityOptions,
  tagOptions,
}: TicketsFilterValueInputProps) => {
  const { t } = useTranslation("contacts");

  // Load all org users so the Owner filter can search across everyone, not just loaded ticket owners.
  const { data: users } = useUsersQuery();

  const stageComboboxOptions = useMemo<ComboboxOption[]>(
    () => stageOptions.map((option) => ({ value: option.value, label: option.label })),
    [stageOptions]
  );
  const priorityComboboxOptions = useMemo<ComboboxOption[]>(
    () => priorityOptions.map((option) => ({ value: option.value, label: option.label })),
    [priorityOptions]
  );
  const ownerSelectOptions = useMemo<MultiSelectOption[]>(
    () => (users ?? []).map((user) => ({ value: user.id, label: formatName(user) || user.id })),
    [users]
  );
  const tagSelectOptions = useMemo<MultiSelectOption[]>(
    () => tagOptions.map((tag) => ({ value: tag, label: tag })),
    [tagOptions]
  );

  switch (filter.field) {
    case "enduserId":
      return (
        <EnduserIdFilterInput
          className={VALUE_INPUT_CLASS_NAME}
          value={typeof filter.value === "string" ? filter.value : undefined}
          onChange={(value) => onChange({ ...filter, value })}
        />
      );
    case "priority": {
      const selected = priorityComboboxOptions.find((option) => Number(option.value) === filter.value);
      return (
        <Combobox
          value={selected}
          onChange={(option) => onChange({ ...filter, value: option ? Number(option.value) : undefined })}
          options={priorityComboboxOptions}
          placeholder={t("Value")}
          searchPlaceholder={t("Search...")}
          className={VALUE_INPUT_CLASS_NAME}
        />
      );
    }
    case "stage": {
      const selected = stageComboboxOptions.find((option) => option.value === filter.value);
      return (
        <Combobox
          value={selected}
          onChange={(option) => onChange({ ...filter, value: option?.value })}
          options={stageComboboxOptions}
          placeholder={t("Value")}
          searchPlaceholder={t("Search...")}
          className={VALUE_INPUT_CLASS_NAME}
        />
      );
    }
    case "owner":
      return (
        <MultiSelect
          className={VALUE_INPUT_CLASS_NAME}
          minPopoverWidth={240}
          options={ownerSelectOptions}
          value={Array.isArray(filter.value) ? filter.value : []}
          onValueChange={(value) => onChange({ ...filter, value })}
          placeholder={t("Select owner")}
        />
      );
    case "tags":
      return (
        <MultiSelect
          className={VALUE_INPUT_CLASS_NAME}
          minPopoverWidth={240}
          options={tagSelectOptions}
          value={Array.isArray(filter.value) ? filter.value : []}
          onValueChange={(value) => onChange({ ...filter, value })}
          placeholder={t("Select tags")}
          creator={{
            render: (input) => t('Add "{{name}}"', { name: input }),
            transform: (input) => input,
            onCreate: () => {},
          }}
        />
      );
    default:
      return null;
  }
};
